import React, { useEffect, useState } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import profile from "../image/profile.png";
import { getToken, removeToken } from "../rtkq/services/localStorageService";
import { StudentNav } from "./studentNav";
import { useGetLoggedUserQuery } from '../rtkq/services/userAuthApi';

export const Navbar = () => {
  const navigate = useNavigate();
  const [showNavbar, setShowNavbar] = useState(false);
  const { access_token } = getToken();
  const [token, setToken] = useState({
    tokens: "",
  });

  // Skip the query if there's no access token
  const { data, isSuccess, isError } = useGetLoggedUserQuery(access_token, {
    skip: !access_token,
  });

  const [userData, setUserData] = useState({
    role: "",
    email: "",
    full_name: "",
  });

  // Handle automatic logout
  const handleLogout = () => {
    removeToken();
    setToken({ tokens: "" });
    setUserData({ role: "", email: "", full_name: "" });
    setShowNavbar(false);
    navigate('/login');
  };

  // Check token validity and handle logout
  useEffect(() => {
    const checkTokenValidity = () => {
      // Only check token validity if we have a token
      if (!access_token) {
        return; // Do nothing if no token exists (user is already logged out)
      }

      // If the API call fails (possibly due to invalid token), logout
      if (isError) {
        handleLogout();
      }
    };

    checkTokenValidity();

    // Set up periodic token check (every minute) only if we have a token
    let tokenCheckInterval;
    if (access_token) {
      tokenCheckInterval = setInterval(checkTokenValidity, 60000);
    }

    return () => {
      if (tokenCheckInterval) {
        clearInterval(tokenCheckInterval);
      }
    };
  }, [access_token, isError]);

  useEffect(() => {
    if (data && isSuccess) {
      setUserData({
        role: data.role,
        full_name: `${data.first_name} ${data.last_name}`,
        email: data.email,
      });
    }
  }, [data, isSuccess]);

  useEffect(() => {
    setToken({
      tokens: access_token,
    });
  }, [access_token]);

  const location = useLocation();

  const toggleStudentNav = () => {
    if (!access_token) {
      navigate('/login');
      return;
    }
    setShowNavbar(!showNavbar);
  };

  // Reset showNavbar when token is removed
  useEffect(() => {
    if (!token.tokens) {
      setShowNavbar(false);
    }
  }, [token]);

  // Helper function to render the appropriate navigation menu
  const renderNavMenu = () => {
    if (!access_token) return null;
    
    const role = userData.role.toLowerCase();
    if (role === 'student' || role === 'teacher') {
      return <StudentNav />;
    }
    return null;
  };

  return (
    <>
      <nav className="bg-white shadow-lg fixed w-full z-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <div className="flex-shrink-0 flex items-center">
              <Link to="/" className="text-red-600 text-2xl font-bold">
                FluenTalk
              </Link>
            </div>

            {/* Main Menu */}
            <div className="hidden md:block">
              <div className="ml-10 flex items-center space-x-8">
                <Link
                  to="/"
                  className={`${
                    location.pathname === '/'
                      ? 'text-green-600 border-b-2 border-green-600'
                      : 'text-red-600 hover:text-green-600'
                  } px-3 py-2 text-sm font-medium transition duration-300`}
                >
                  Home
                </Link>
                <Link
                  to="/pricing"
                  className={`${
                    location.pathname === '/pricing'
                      ? 'text-green-600 border-b-2 border-green-600'
                      : 'text-red-600 hover:text-green-600'
                  } px-3 py-2 text-sm font-medium transition duration-300`}
                >
                  Pricing
                </Link>
                <Link
                  to="/contact"
                  className={`${
                    location.pathname === '/contact'
                      ? 'text-green-600 border-b-2 border-green-600'
                      : 'text-red-600 hover:text-green-600'
                  } px-3 py-2 text-sm font-medium transition duration-300`}
                >
                  Contact Us
                </Link>

                {/* {userData.role.toLowerCase() !== 'admin' && (
                  <Link
                    to="/apply_student_admission"
                    className={`${
                      location.pathname === '/apply_student_admission'
                        ? 'text-green-600 border-b-2 border-green-600'
                        : 'text-red-600 hover:text-green-600'
                    } px-3 py-2 text-sm font-medium transition duration-300`}
                  >
                    Apply Admission
                  </Link>
                )} */}

                {!access_token ? (
                  <Link
                    to="/login"
                    className="bg-red-600 text-white hover:bg-red-700 px-4 py-2 rounded-lg text-sm font-medium transition duration-300"
                  >
                    Login
                  </Link>
                ) : (
                  <div className="relative">
                    <button
                      onClick={toggleStudentNav}
                      className="group focus:outline-none"
                    >
                      <img
                        src={profile}
                        alt="profile"
                        className="w-8 h-8 rounded-full border-2 border-green-600 transition duration-300 transform group-hover:scale-110"
                      />
                    </button>
                    {showNavbar && access_token && (
                      <div className="absolute right-0 mt-2 py-2 w-48 bg-white rounded-lg shadow-xl">
                        {renderNavMenu()}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden flex items-center">
              <button
                onClick={toggleStudentNav}
                className="inline-flex items-center justify-center p-2 rounded-md text-red-600 hover:text-green-600 focus:outline-none"
              >
                <svg
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d={showNavbar ? 'M6 18L18 6M6 6l12 12' : 'M4 6h16M4 12h16M4 18h16'}
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {showNavbar && (
          <div className="md:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
              <Link
                to="/"
                className="block px-3 py-2 text-red-600 hover:text-green-600 font-medium"
              >
                Home
              </Link>
              <Link
                to="/pricing"
                className="block px-3 py-2 text-red-600 hover:text-green-600 font-medium"
              >
                Pricing
              </Link>
              <Link
                to="/contact"
                className="block px-3 py-2 text-red-600 hover:text-green-600 font-medium"
              >
                Contact Us
              </Link>
              {/* {userData.role.toLowerCase() !== 'admin' && (
                <Link
                  to="/apply_student_admission"
                  className="block px-3 py-2 text-red-600 hover:text-green-600 font-medium"
                >
                  Apply Admission
                </Link>
              )} */}
              {access_token && renderNavMenu()}
            </div>
          </div>
        )}
      </nav>

      {/* Spacer for fixed navbar */}
      <div className="h-16"></div>

      <Outlet />
    </>
  );
};